import React, { useState, useEffect, useMemo } from "react";
import { Typography, Tooltip } from "@material-ui/core";
import IconButton from "@mui/material/IconButton";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ReactMarkdown from "react-markdown";
import { makeStyles } from "@material-ui/core/styles";
import { processChildren } from "./CitationPopup";
import PipelineStatus from "./PipelineStatus";
import { loadSettings } from "../pages/UserSettings";

const useStyles = makeStyles((theme) => ({
  jumpingDots: {
    display: "flex",
    alignItems: "center",
    marginLeft: "5px",
  },
  dot: {
    width: "8px",
    height: "8px",
    backgroundColor: "#FFC629",
    borderRadius: "50%",
    margin: "0 2px",
    animation: "$jump 1.4s infinite ease-in-out both",
  },
  dot1: {
    animationDelay: "-0.32s",
  },
  dot2: {
    animationDelay: "-0.16s",
  },
  dot3: {
    animationDelay: "0s",
  },
  "@keyframes jump": {
    "0%, 80%, 100%": {
      transform: "scale(0)",
    },
    "40%": {
      transform: "scale(1.0)",
    },
  },
  errorText: {
    color: "#ff6b6b",
    fontStyle: "italic",
    marginTop: theme.spacing(1),
  },
}));

const StreamingMessage = ({
  message,
  isStreaming,
  isLoading,
  hasError,
  toggleExpand,
  isExpanded,
  citations,
  pipelineSteps,
  pipelineComplete,
}) => {
  const classes = useStyles();
  const alwaysExpand = loadSettings().expand_responses;
  // Ensure message is always a string to prevent length errors
  const safeMessage = message || "";
  const [displayMessage, setDisplayMessage] = useState(safeMessage);
  const MAX_CHAR_COUNT = 750;
  const isLongMessage = !alwaysExpand && safeMessage.length > MAX_CHAR_COUNT;

  useEffect(() => {
    // Use safeMessage instead of message to prevent undefined errors
    if (!isExpanded && isLongMessage && !isStreaming) {
      setDisplayMessage(safeMessage.substring(0, MAX_CHAR_COUNT) + " . . .");
    } else {
      setDisplayMessage(safeMessage);
    }
  }, [safeMessage, isStreaming, isExpanded, isLongMessage]);

  // Derive source count from the analyzing_sources step if available
  const analyzingStep = pipelineSteps?.find((s) => s.step === "analyzing_sources");
  const sourceCount = analyzingStep
    ? parseInt(analyzingStep.message.match(/\d+/)?.[0], 10) || undefined
    : undefined;

  const hasPipelineSteps = pipelineSteps && pipelineSteps.length > 0;

  const markdownComponents = useMemo(() => ({
    h3: ({ node, ...props }) => (
      <Typography
        variant="h6"
        style={{
          fontWeight: "bold",
          color: "#FFC629",
          marginTop: 8,
          marginBottom: 4,
        }}
        {...props}
      />
    ),
    strong: ({ node, children, ...props }) => (
      <strong style={{ color: "#FFC629" }} {...props}>
        {processChildren(children, citations)}
      </strong>
    ),
    em: ({ node, children, ...props }) => (
      <em {...props}>{processChildren(children, citations)}</em>
    ),
    p: ({ node, children, ...props }) => (
      <Typography
        style={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "0.9em",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "white",
          marginBottom: 8,
        }}
        {...props}
      >
        {processChildren(children, citations)}
      </Typography>
    ),
    ul: ({ node, ...props }) => (
      <ul
        style={{
          paddingLeft: "1.2em",
          marginTop: 4,
          marginBottom: 8,
          color: "white",
          fontFamily: "Poppins, sans-serif",
          fontSize: "0.9em",
        }}
        {...props}
      />
    ),
    ol: ({ node, ...props }) => (
      <ol
        style={{
          paddingLeft: "1.2em",
          marginTop: 4,
          marginBottom: 8,
          color: "white",
          fontFamily: "Poppins, sans-serif",
          fontSize: "0.9em",
        }}
        {...props}
      />
    ),
    li: ({ node, children, ...props }) => (
      <li style={{ marginBottom: 4 }} {...props}>
        {processChildren(children, citations)}
      </li>
    ),
    a: ({ node, href, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#64b5f6",
          textDecoration: "underline",
        }}
        {...props}
      />
    ),
    code: ({ node, inline, className, children, ...props }) =>
      inline ? (
        <code
          style={{
            backgroundColor: "#333",
            color: "#FFC629",
            borderRadius: 4,
            padding: "2px 4px",
            fontSize: "0.85em",
          }}
          {...props}
        >
          {children}
        </code>
      ) : (
        <pre
          style={{
            backgroundColor: "#1a1a1a",
            color: "#FFC629",
            padding: "8px 12px",
            borderRadius: 6,
            overflowX: "auto",
            fontSize: "0.85em",
            marginBottom: 8,
          }}
          {...props}
        >
          <code>{children}</code>
        </pre>
      ),
  }), [citations]);

  if (isLoading && !safeMessage) {
    return (
      <div className={classes.jumpingDots}>
        <div className={`${classes.dot} ${classes.dot1}`} />
        <div className={`${classes.dot} ${classes.dot2}`} />
        <div className={`${classes.dot} ${classes.dot3}`} />
      </div>
    );
  }

  return (
    <>
      {hasPipelineSteps && (
        <PipelineStatus
          steps={pipelineSteps}
          isComplete={!!pipelineComplete}
          sourceCount={sourceCount}
        />
      )}
      <ReactMarkdown
        children={displayMessage}
        components={markdownComponents}
      />
      
      {isStreaming && !hasPipelineSteps && (
        <div className={classes.jumpingDots}>
          <span className={`${classes.dot} ${classes.dot1}`} />
          <span className={`${classes.dot} ${classes.dot2}`} />
          <span className={`${classes.dot} ${classes.dot3}`} />
        </div>
      )}
      
      {hasError && (
        <Typography className={classes.errorText}>
          Connection interrupted. The response may be incomplete.
        </Typography>
      )}
      
      {isLongMessage && !isStreaming && (
        <Tooltip title={isExpanded ? "Read less" : "Read more"} placement="bottom">
          <IconButton
            onClick={toggleExpand}
            style={{
              color: "rgba(255,255,255,0.4)",
              padding: 2,
              marginTop: 4,
            }}
            size="small"
          >
            {isExpanded ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default StreamingMessage; 